import { DeleteOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, App, Button, Card, Col, DatePicker, Flex, Input, InputNumber, Progress, Result, Row, Select, Skeleton, Space, Table, Tag, Typography } from "antd";
import type { Dayjs } from "dayjs";
import { useState } from "react";
import { useParams } from "react-router";
import { ApiError, api, unwrap } from "@/api/client";
import { useErrorText } from "@/api/errors";
import { useAuth } from "@/auth/AuthProvider";
import { ScanInput } from "@/components/ScanInput";
import { useTabTitle } from "@/components/tabs/WorkTabs";
import { useI18n } from "@/i18n";
import { conditionOptions } from "../inventory/conditions";
import { CountStatusTag, LocationKindTag, type StockCount } from "./common";

type CountLocation = NonNullable<StockCount["locationList"]>[number];
type CountLine = NonNullable<StockCount["lines"]>[number];
type Condition = "sellable" | "damaged" | "quarantine";
type Lot = { id?: string; lotNo?: string; expiresOn?: string | null };
type ScannedSku = { id: string; code: string; name: string; tracking: "none" | "lot" | "serial"; lots: Lot[] };
type Entry = { key: string; skuId: string; code: string; name: string; tracking: ScannedSku["tracking"]; condition: Condition; lotNo?: string; expiresOn?: string; quantity: number; serials: string[] };

const conditionOf = (kind: string): Condition => (kind === "damaged" ? "damaged" : kind === "quarantine" ? "quarantine" : "sellable");

/**
 * One stock count: pick a location (scan its code), scan what is there, send.
 * Differences go to the variance location; nothing is written off here.
 */
export function CountDetail() {
  const { countID = "" } = useParams();
  const { t, formatNumber, formatDateTime } = useI18n();
  const { me, can } = useAuth();
  const { message, modal } = App.useApp();
  const errorText = useErrorText();
  const queryClient = useQueryClient();
  const tz = me?.workspace?.timezone;
  const manage = can("warehouse.manage");
  const [locationId, setLocationId] = useState<string | null>(null);
  const [entries, setEntries] = useState<Entry[]>([]);
  const [lotSku, setLotSku] = useState<ScannedSku | null>(null);
  const [serialKey, setSerialKey] = useState<string | null>(null);

  const count = useQuery({
    queryKey: ["counts", "detail", countID],
    queryFn: () => unwrap(api.GET("/v1/counts/{countID}", { params: { path: { countID } } })),
    retry: (n, err) => !(err instanceof ApiError && err.status === 404) && n < 2,
  });
  useTabTitle(count.data?.number);
  const c = count.data;
  const locs = c?.locationList ?? [];
  const current = locs.find((l) => l.locationId === locationId) ?? null;

  const where = useQuery({
    queryKey: ["counts", "location", countID, locationId],
    queryFn: () => unwrap(api.GET("/v1/counts/{countID}/locations/{locationID}", { params: { path: { countID, locationID: locationId! } } })),
    enabled: !!locationId,
  });

  const refresh = (res?: StockCount) => {
    if (res) queryClient.setQueryData(["counts", "detail", countID], res);
    void queryClient.invalidateQueries({ queryKey: ["counts"] });
    void queryClient.invalidateQueries({ queryKey: ["inventory"] });
    void queryClient.invalidateQueries({ queryKey: ["locations"] });
    void queryClient.invalidateQueries({ queryKey: ["variance"] });
  };
  const choose = (l: CountLocation | null) => {
    setLocationId(l?.locationId ?? null);
    setEntries([]);
    setLotSku(null);
    setSerialKey(null);
  };

  const submit = useMutation({
    mutationFn: (lines: Entry[]) =>
      unwrap(
        api.POST("/v1/counts/{countID}/locations/{locationID}", {
          params: { path: { countID, locationID: locationId! } },
          body: {
            lines: lines.map((e) => ({
              skuId: e.skuId,
              condition: e.condition,
              ...(e.tracking === "serial" ? { serials: e.serials } : { quantity: e.quantity }),
              ...(e.lotNo ? { lotNo: e.lotNo, ...(e.expiresOn ? { expiresOn: e.expiresOn } : {}) } : {}),
            })),
          },
        }),
      ),
    onSuccess: (res) => {
      void message.success(t("wms.count.submitted", { code: current?.code ?? "" }));
      choose(null);
      refresh(res);
    },
    onError: (err) => void message.error(errorText(err)),
  });
  const recount = useMutation({
    mutationFn: (locationID: string) => unwrap(api.POST("/v1/counts/{countID}/locations/{locationID}/recount", { params: { path: { countID, locationID } } })),
    onSuccess: (res) => refresh(res),
    onError: (err) => void message.error(errorText(err)),
  });
  const end = useMutation({
    mutationFn: (how: "close" | "cancel") =>
      unwrap(how === "close" ? api.POST("/v1/counts/{countID}/close", { params: { path: { countID } } }) : api.POST("/v1/counts/{countID}/cancel", { params: { path: { countID } } })),
    onSuccess: (res) => refresh(res),
    onError: (err) => void message.error(errorText(err)),
  });

  if (count.isError) {
    return count.error instanceof ApiError && count.error.status === 404 ? <Result status="404" title={t("page.notFound.title")} /> : <Result status="error" title={errorText(count.error)} />;
  }
  if (!c) return <Skeleton active />;
  const open = c.status === "open";
  const defaultCondition = conditionOf(current?.kind ?? "storage");

  const add = (sku: ScannedSku, lot?: { lotNo: string; expiresOn?: string }, qty = 1) => {
    const key = `${sku.id}/${lot?.lotNo ?? ""}`;
    setEntries((list) => {
      const hit = list.find((e) => e.key === key);
      if (hit) return list.map((e) => (e.key === key ? { ...e, quantity: e.quantity + qty } : e));
      return [...list, { key, skuId: sku.id, code: sku.code, name: sku.name, tracking: sku.tracking, condition: defaultCondition, quantity: qty, serials: [], ...lot }];
    });
    return key;
  };

  // Resolves true or false: the scan field beeps accordingly.
  const scan = async (code: string): Promise<boolean> => {
    if (!current) {
      const l = locs.find((x) => x.code.toUpperCase() === code.toUpperCase());
      if (!l) void message.warning(t("wms.count.notInCount", { code }));
      else if (l.status !== "pending") void message.warning(t("wms.count.alreadyCounted", { code: l.code }));
      else choose(l);
      return !!l && l.status === "pending";
    }
    try {
      const hit = await unwrap(api.GET("/v1/scan", { params: { query: { warehouseId: c.warehouseId, code } } }));
      if (hit.type === "sku" && hit.sku) {
        const sku: ScannedSku = { id: hit.sku.id!, code: hit.sku.code!, name: hit.sku.name!, tracking: hit.sku.tracking ?? "none", lots: hit.sku.lots ?? [] };
        if (sku.tracking === "lot") {
          setLotSku(sku);
          setSerialKey(null);
        } else if (sku.tracking === "serial") {
          setSerialKey(add(sku, undefined, 0));
        } else {
          setSerialKey(null);
          add(sku);
        }
        return true;
      }
      if (serialKey) {
        addSerial(code);
        return true;
      }
      void message.warning(t("error.code_not_found"));
      return false;
    } catch (err) {
      // An unknown code while a serial SKU is open is a unit not on the books.
      if (serialKey && err instanceof ApiError && err.status === 404) {
        addSerial(code);
        return true;
      }
      void message.error(errorText(err));
      return false;
    }
  };
  const addSerial = (no: string) =>
    setEntries((list) => list.map((e) => (e.key === serialKey && !e.serials.includes(no) ? { ...e, serials: [...e.serials, no], quantity: e.serials.length + 1 } : e)));

  const confirmSubmit = (lines: Entry[]) =>
    modal.confirm({
      title: t("wms.count.submit"),
      content: lines.length === 0 ? t("wms.count.emptyConfirm", { code: current!.code }) : t("wms.count.submitConfirm", { code: current!.code, n: lines.length }),
      okText: t("wms.count.submit"),
      cancelText: t("common.cancel"),
      onOk: () => submit.mutateAsync(lines),
    });

  return (
    <>
      <Flex justify="space-between" align="center" wrap gap={12} style={{ marginBottom: 16 }}>
        <Space align="center" size={12} wrap>
          <Typography.Title level={2} className="z-num" style={{ margin: 0, fontSize: 28 }}>
            {c.number}
          </Typography.Title>
          <Tag color={c.kind === "full" ? "geekblue" : "cyan"}>{t(`wms.count.kind.${c.kind}`)}</Tag>
          <CountStatusTag status={c.status} />
          {c.blind && <Tag>{t("wms.count.blind")}</Tag>}
          <Typography.Text type="secondary">
            {c.warehouseName} · {c.createdName} · {formatDateTime(c.createdAt, tz)}
          </Typography.Text>
        </Space>
        {open && manage && (
          <Space>
            {c.counted === 0 && (
              <Button danger loading={end.isPending} onClick={() => end.mutate("cancel")}>
                {t("wms.count.cancel")}
              </Button>
            )}
            <Button
              type="primary"
              loading={end.isPending}
              onClick={() =>
                modal.confirm({
                  title: t("wms.count.close"),
                  content: t("wms.count.closeConfirm", { n: c.locations - c.counted }),
                  okText: t("wms.count.close"),
                  cancelText: t("common.cancel"),
                  onOk: () => end.mutateAsync("close"),
                })
              }
            >
              {t("wms.count.close")}
            </Button>
          </Space>
        )}
      </Flex>
      {c.note && <Typography.Paragraph type="secondary">{c.note}</Typography.Paragraph>}
      <Progress percent={Math.round((c.counted / Math.max(c.locations, 1)) * 100)} format={() => `${formatNumber(c.counted)}/${formatNumber(c.locations)}`} style={{ marginBottom: 16 }} />
      {manage && (c.shortUnits > 0 || c.overUnits > 0) && (
        <Alert type="info" showIcon style={{ marginBottom: 16 }} title={t("wms.count.toVariance", { short: formatNumber(c.shortUnits), over: formatNumber(c.overUnits) })} />
      )}
      <Row gutter={[16, 16]}>
        {open && (
          <Col xs={24} xl={10}>
            <Card size="small" title={current ? t("wms.count.counting", { code: current.code }) : t("wms.count.scanLocation")}>
              <Space orientation="vertical" size={12} style={{ width: "100%" }}>
                <Typography.Text type="secondary">{t(current ? "wms.count.scanItems" : "wms.count.scanLocationHelp")}</Typography.Text>
                <ScanInput onScan={scan} />
                {current && (
                  <>
                    <Flex gap={8} align="center" wrap>
                      <LocationKindTag kind={current.kind} />
                      {current.round > 1 && <Tag color="warning">{t("wms.count.round", { n: current.round })}</Tag>}
                    </Flex>
                    {where.data?.expected && (
                      <Alert
                        type="info"
                        title={t("wms.count.books")}
                        description={
                          where.data.expected.length === 0
                            ? t("wms.count.booksEmpty")
                            : where.data.expected.map((e) => `${e.skuCode}${e.lotNo ? ` (${e.lotNo})` : ""}: ${formatNumber(e.quantity)}`).join(" · ")
                        }
                      />
                    )}
                    {lotSku && <LotPicker sku={lotSku} onPick={(lot, qty) => (add(lotSku, lot, qty), setLotSku(null))} onCancel={() => setLotSku(null)} />}
                    <Table<Entry>
                      size="small"
                      rowKey="key"
                      dataSource={entries}
                      pagination={false}
                      locale={{ emptyText: t("wms.count.nothingYet") }}
                      columns={[
                        {
                          title: t("catalog.code"),
                          dataIndex: "code",
                          render: (v: string, e) => (
                            <Space orientation="vertical" size={0}>
                              <span className="z-num">{v}</span>
                              {e.lotNo && <Typography.Text type="secondary" style={{ fontSize: 12 }}>{`${t("inventory.bin.lot")} ${e.lotNo}`}</Typography.Text>}
                              {e.tracking === "serial" && (
                                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                  {e.serials.join(", ") || t("wms.count.scanSerials")}
                                </Typography.Text>
                              )}
                            </Space>
                          ),
                        },
                        {
                          title: t("inventory.condition"),
                          dataIndex: "condition",
                          width: 130,
                          render: (v: Condition, e) => (
                            <Select<Condition>
                              size="small"
                              value={v}
                              style={{ width: 120 }}
                              options={conditionOptions(t)}
                              onChange={(x) => setEntries((l) => l.map((y) => (y.key === e.key ? { ...y, condition: x } : y)))}
                            />
                          ),
                        },
                        {
                          title: t("wms.in.quantity"),
                          dataIndex: "quantity",
                          width: 100,
                          render: (v: number, e) =>
                            e.tracking === "serial" ? (
                              <span className="z-num">{e.serials.length}</span>
                            ) : (
                              <InputNumber size="small" min={0} max={10_000_000} precision={0} value={v} onChange={(x) => setEntries((l) => l.map((y) => (y.key === e.key ? { ...y, quantity: x ?? 0 } : y)))} style={{ width: 90 }} />
                            ),
                        },
                        {
                          width: 40,
                          render: (_, e) => <Button size="small" type="text" icon={<DeleteOutlined />} aria-label={t("common.delete")} onClick={() => setEntries((l) => l.filter((y) => y.key !== e.key))} />,
                        },
                      ]}
                    />
                    <Flex gap={8} wrap>
                      <Button type="primary" disabled={entries.length === 0} loading={submit.isPending} onClick={() => confirmSubmit(entries)}>
                        {t("wms.count.submit")}
                      </Button>
                      <Button disabled={entries.length > 0} onClick={() => confirmSubmit([])}>
                        {t("wms.count.empty1")}
                      </Button>
                      <Button type="text" onClick={() => choose(null)}>
                        {t("common.cancel")}
                      </Button>
                    </Flex>
                  </>
                )}
              </Space>
            </Card>
          </Col>
        )}
        <Col xs={24} xl={open ? 14 : 24}>
          <Card size="small" title={t("wms.count.locations")}>
            <Table<CountLocation>
              size="small"
              rowKey="locationId"
              dataSource={locs}
              pagination={locs.length > 50 ? { pageSize: 50, showSizeChanger: false } : false}
              onRow={(l) => ({
                onClick: () => open && l.status === "pending" && choose(l),
                style: { cursor: open && l.status === "pending" ? "pointer" : undefined, background: l.locationId === locationId ? "var(--z-mint)" : undefined },
              })}
              columns={[
                { title: t("inventory.bin.location"), dataIndex: "code", render: (v: string) => <Typography.Text strong className="z-num">{v}</Typography.Text> },
                {
                  title: t("common.status"),
                  dataIndex: "status",
                  width: 170,
                  render: (s: CountLocation["status"], l) => (
                    <Space size={4}>
                      <Tag color={s === "counted" ? "success" : s === "skipped" ? "default" : "processing"}>{t(`wms.count.loc.${s}`)}</Tag>
                      {l.round > 1 && <Tag color="warning">{t("wms.count.round", { n: l.round })}</Tag>}
                    </Space>
                  ),
                },
                { title: t("wms.count.countedBy"), dataIndex: "countedName", width: 150, render: (v: string) => v || "—" },
                ...(manage || !c.blind
                  ? [
                      {
                        title: t("wms.count.difference"),
                        width: 130,
                        align: "right" as const,
                        render: (_: unknown, l: CountLocation) => (
                          <Space size={4}>
                            {l.shortUnits > 0 && <Tag color="warning" className="z-num">−{l.shortUnits}</Tag>}
                            {l.overUnits > 0 && <Tag color="processing" className="z-num">+{l.overUnits}</Tag>}
                          </Space>
                        ),
                      },
                    ]
                  : []),
                ...(manage
                  ? [
                      {
                        width: 120,
                        render: (_: unknown, l: CountLocation) =>
                          l.status === "counted" && (
                            <Button
                              size="small"
                              loading={recount.isPending && recount.variables === l.locationId}
                              onClick={(e) => {
                                e.stopPropagation();
                                recount.mutate(l.locationId);
                              }}
                            >
                              {t("wms.count.recount")}
                            </Button>
                          ),
                      },
                    ]
                  : []),
              ]}
            />
          </Card>
        </Col>
        {(c.lines?.length ?? 0) > 0 && (
          <Col span={24}>
            <Card size="small" title={t("wms.count.results")}>
              <Table<CountLine>
                size="small"
                rowKey={(l) => `${l.locationId}/${l.round}/${l.skuId}/${l.lotId ?? ""}/${l.condition}`}
                dataSource={c.lines}
                pagination={(c.lines?.length ?? 0) > 50 ? { pageSize: 50, showSizeChanger: false } : false}
                columns={[
                  { title: t("inventory.bin.location"), dataIndex: "locationCode", width: 130, render: (v: string) => <span className="z-num">{v}</span> },
                  { title: t("wms.count.roundCol"), dataIndex: "round", width: 80, align: "right" },
                  {
                    title: t("catalog.code"),
                    dataIndex: "skuCode",
                    render: (v: string, l) => (
                      <Space orientation="vertical" size={0}>
                        <span className="z-num">{v}</span>
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>{l.skuName}</Typography.Text>
                      </Space>
                    ),
                  },
                  { title: t("inventory.bin.lot"), dataIndex: "lotNo", width: 120, render: (v: string | null) => v ?? "—" },
                  { title: t("inventory.condition"), dataIndex: "condition", width: 120, render: (v: Condition) => t(`inventory.condition.${v}`) },
                  { title: t("wms.count.system"), dataIndex: "systemQty", width: 100, align: "right", render: (v: number) => <span className="z-num">{formatNumber(v)}</span> },
                  { title: t("wms.count.counted"), dataIndex: "countedQty", width: 100, align: "right", render: (v: number) => <span className="z-num">{formatNumber(v)}</span> },
                  {
                    title: t("wms.count.difference"),
                    width: 110,
                    align: "right",
                    render: (_, l) => {
                      const d = l.countedQty - l.systemQty;
                      return (
                        <Typography.Text className="z-num" type={d === 0 ? "secondary" : d < 0 ? "warning" : undefined}>
                          {d > 0 ? `+${d}` : d}
                        </Typography.Text>
                      );
                    },
                  },
                ]}
              />
            </Card>
          </Col>
        )}
      </Row>
    </>
  );
}

/** Which lot a scanned lot-tracked SKU belongs to: a known lot, or a new one with its expiry. */
function LotPicker({ sku, onPick, onCancel }: { sku: ScannedSku; onPick: (lot: { lotNo: string; expiresOn?: string }, qty: number) => void; onCancel: () => void }) {
  const { t } = useI18n();
  const [lotNo, setLotNo] = useState<string | undefined>(sku.lots[0]?.lotNo);
  const [newLot, setNewLot] = useState("");
  const [expires, setExpires] = useState<Dayjs | null>(null);
  const [qty, setQty] = useState<number | null>(1);
  const fresh = lotNo === "__new";
  const ready = (qty ?? 0) > 0 && (fresh ? newLot.trim() && expires : lotNo);
  return (
    <Card size="small" type="inner" title={<span className="z-num">{sku.code}</span>} extra={<Button size="small" type="text" onClick={onCancel}>{t("common.cancel")}</Button>}>
      <Space orientation="vertical" size={8} style={{ width: "100%" }}>
        <Select<string>
          value={lotNo}
          onChange={setLotNo}
          placeholder={t("inventory.bin.lot")}
          options={[
            ...sku.lots.map((l) => ({ value: l.lotNo!, label: `${l.lotNo}${l.expiresOn ? ` · ${l.expiresOn}` : ""}` })),
            { value: "__new", label: t("wms.count.newLot") },
          ]}
        />
        {fresh && (
          <Flex gap={8}>
            <Input placeholder={t("wms.in.lotNo")} value={newLot} onChange={(e) => setNewLot(e.target.value)} />
            <DatePicker placeholder={t("wms.in.expiresOn")} value={expires} onChange={setExpires} />
          </Flex>
        )}
        <InputNumber prefix={t("wms.in.quantity")} min={1} max={10_000_000} precision={0} value={qty} onChange={setQty} style={{ width: "100%" }} />
        <Button
          type="primary"
          block
          disabled={!ready}
          onClick={() => onPick(fresh ? { lotNo: newLot.trim(), expiresOn: expires!.format("YYYY-MM-DD") } : { lotNo: lotNo! }, qty ?? 1)}
        >
          {t("common.add")}
        </Button>
      </Space>
    </Card>
  );
}
